import React, { useCallback, useEffect, useState } from "react";
import { getNotifications, notifyInvoice, getNotificationsStatus } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS = {
  sent: { icon: CheckCircle2, cls: "text-emerald-500", label: "Sent" },
  failed: { icon: XCircle, cls: "text-red-500", label: "Failed" },
  skipped: { icon: MinusCircle, cls: "text-muted-foreground", label: "Skipped" },
};

export const SupplierNotifications = ({ inv, onChanged }) => {
  const { user } = useApp();
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => getNotifications({ invoice_id: inv.id, limit: 20 }).then((r) => setItems(r.items)), [inv.id]);
  useEffect(() => { load(); getNotificationsStatus().then(setCfg); }, [load]);

  const send = async (event) => {
    setBusy(event);
    try {
      const r = await notifyInvoice(inv.id, event, user);
      toast.success(`Emailed ${r.to}: ${r.subject}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
      await load();
    } finally {
      setBusy(null);
    }
  };

  const events = [["received", true], ["approved", inv.status === "approved"], ["paid", !!inv.payment]];

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="supplier-notifications">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium"><Mail className="h-4 w-4 text-rose-400" /> Supplier emails</div>
        <div className="flex gap-1.5">
          {events.map(([ev, ok]) => (
            <Button key={ev} data-testid={`notify-${ev}-btn`} size="sm" variant="outline" className="h-7 text-xs capitalize" disabled={!ok || busy || !cfg?.configured} onClick={() => send(ev)}>
              {busy === ev ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />} {ev}
            </Button>
          ))}
        </div>
      </div>
      {!items ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="notifications-empty">No emails yet. Status emails go out automatically when a supplier email is on file (Suppliers page).</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((n) => {
            const s = STATUS[n.status] || STATUS.skipped;
            return (
              <li key={n.id} data-testid="notification-item" className="flex items-center justify-between gap-3 text-xs">
                <span className="flex items-center gap-1.5 min-w-0">
                  <s.icon className={cn("h-3.5 w-3.5 shrink-0", s.cls)} />
                  <span className="capitalize font-medium">{n.event}</span>
                  <span className="text-muted-foreground truncate">{n.status === "sent" ? `→ ${n.to}` : n.error}</span>
                </span>
                <span className="font-mono text-muted-foreground whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
